import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useApp } from "../AppContext";
import { useAuth } from "../AuthContext";
import { applicaMapping, salvaTemplate, scegliCartella, suggerisciMapping } from "../api";
import EstrazioneModal from "../EstrazioneModal";
import { MODULI, moduloDaPercorso } from "../moduli";

const TIPI = [
  { valore: "manuale", etichetta: "Manuale / vuoto" },
  { valore: "sorgente", etichetta: "Da colonna sorgente" },
  { valore: "fisso", etichetta: "Valore fisso" },
];

// Alcuni file cliente hanno colonne "striminzite" che contengono più
// informazioni insieme in una sola stringa. Due modalità di estrazione:
//  - "separatore" (default, anche quando il campo manca, per compatibilità
//    con l'esistente): spacca il testo su un carattere (es. "Article
//    description" = "Montblanc Extreme 3.0 DocCase Thin Bk" -> ultima parola
//    "Bk" = il colore) e prende la parte all'indice richiesto (0-based,
//    negativo = da fondo, come in Python: -1 = ultima parola).
//  - "posizione": taglia un intervallo di CARATTERI, per codici compatti
//    senza separatori (es. "FVVE54619536" -> ultimi 3 caratteri "536" = il
//    colore). da/a sono indici di carattere, stessa convenzione negativa.
// Stessa logica duplicata lato backend in routes/api.js /applica-mapping —
// se la cambi qui, cambiala anche lì.
function estraiValore(valoreOrigine, estrazione) {
  const testo = String(valoreOrigine ?? "");
  // Vedi lo stesso commento in routes/api.js: un "a capo" dentro al valore
  // (vedi routes/pdfEstrattore.js unisciPezziCella) serve SOLO a poter
  // isolare ogni riga fisica con "Estrai una parte" → A capo — in ogni
  // altro caso va appiattito, anche quando il pezzo scelto non è
  // esattamente sul confine dell'a capo, coerente con quello che poi
  // esporta il server.
  const separaSuACapo = estrazione && estrazione.modalita !== "posizione" && (estrazione.separatore || " ") === "\n";
  const base = separaSuACapo ? testo : testo.replace(/\n/g, " ").trim();
  if (!estrazione) return base;
  // "regex": per testate a lunghezza variabile (es. il titolo Dior, dove le parole
  // cambiano da PDF a PDF): prende il gruppo "gruppo" (default 1) del primo match;
  // se non combacia restituisce vuoto, mai un pezzo a caso.
  if (estrazione.modalita === "regex") {
    try {
      const m = new RegExp(estrazione.pattern || "").exec(testo);
      return m ? String(m[Number.isFinite(Number(estrazione.gruppo)) ? Number(estrazione.gruppo) : 1] ?? "").trim() : "";
    } catch (e) {
      return "";
    }
  }
  if (estrazione.modalita === "posizione") {
    const len = base.length;
    let da = Number(estrazione.da);
    let a = Number(estrazione.a);
    if (!Number.isFinite(da)) da = 0;
    if (!Number.isFinite(a)) a = len - 1;
    const s = da < 0 ? Math.max(0, len + da) : Math.min(da, len);
    const e = a < 0 ? len + a : Math.min(a, len - 1);
    if (e < s) return "";
    return base.slice(s, e + 1);
  }
  const separatore = estrazione.separatore || " ";
  const parti = separatore === ""
    ? [base]
    : base.split(separatore).map((p) => p.trim()).filter((p) => p !== "");
  if (parti.length === 0) return "";
  // "minParti" (opzionale): se il testo ha meno pezzi di questa soglia,
  // vuoto invece di indicizzare — vedi lo stesso commento in
  // routes/ordini.js per il "perché" (un pezzo opzionale in mezzo che a
  // volte manca del tutto, non solo più corto).
  const minParti = Number(estrazione.minParti);
  if (Number.isFinite(minParti) && parti.length < minParti) return "";
  let indice = Number(estrazione.indice);
  if (!Number.isFinite(indice)) indice = -1;
  const i = indice < 0 ? parti.length + indice : indice;
  // "indiceA" (opzionale): intervallo di pillole da "indice" a "indiceA"
  // comprese, riunite con lo stesso separatore (stessa logica duplicata lato
  // backend e in OrdiniMapping.jsx).
  const indiceA = estrazione.indiceA == null ? NaN : Number(estrazione.indiceA);
  if (Number.isFinite(indiceA)) {
    if (i < 0 || i >= parti.length) return "";
    const fine = indiceA < 0 ? parti.length + indiceA : indiceA;
    return parti.slice(i, Math.min(fine, parti.length - 1) + 1).join(separatore === "\n" ? " " : separatore).trim();
  }
  return parti[i] ?? "";
}

// "Unisci campi": costruisce colonne "virtuali" in più, con nome scelto
// dall'utente, unendo due (o più) colonne già esistenti del file cliente
// (anche altri campi combinati, per poterli incatenare) con un separatore
// a scelta — es. il vero numero ordine cliente = colonna "Ordine" + colonna
// "Riga". Applicate PRIMA del mapping vero e proprio: da quel momento si
// comportano come una colonna qualunque del file e si possono scegliere
// come "colonna sorgente" per un campo del tracciato.
// Stessa logica duplicata lato backend in routes/api.js /applica-mapping —
// se la cambi qui, cambiala anche lì.
function applicaCampiCombinatiRiga(rigaOrigine, campiCombinati) {
  if (!campiCombinati || campiCombinati.length === 0) return rigaOrigine;
  const estesa = { ...rigaOrigine };
  for (const combinato of campiCombinati) {
    const pezzi = (combinato.sorgenti || [])
      .map((s) => String(estesa[s] ?? "").trim())
      .filter((p) => p !== "");
    estesa[combinato.nome] = pezzi.join(combinato.separatore ?? "");
  }
  return estesa;
}

// Stessa logica di formatoNumeroVirgola in routes/api.js /applica-mapping:
// "1.0350" -> "1,0350" solo per numeri semplici, se la regola lo chiede.
function formatoNumeroVirgola(valore, regola) {
  if (!regola || !regola.numeroVirgola) return valore;
  const testo = String(valore ?? "").trim();
  return /^-?\d+\.\d+$/.test(testo) ? testo.replace(".", ",") : valore;
}

function mappaRigaAnteprima(rigaOrigine, mapping, colonne) {
  const out = {};
  for (const col of colonne) {
    const regola = mapping[col.chiave];
    if (!regola || regola.tipo === "manuale") out[col.chiave] = "";
    else if (regola.tipo === "fisso") out[col.chiave] = regola.valore ?? "";
    else if (regola.tipo === "sorgente") out[col.chiave] = formatoNumeroVirgola(estraiValore(rigaOrigine[regola.colonna], regola.estrazione), regola);
    else out[col.chiave] = "";
  }
  return out;
}

export default function Mapping() {
  const navigate = useNavigate();
  const location = useLocation();
  const modulo = moduloDaPercorso(location.pathname);
  const percorsoBase = MODULI[modulo].percorsoBase;
  const {
    tracciato, fileCaricato, lottoPdf, templateAttivo, setTemplateAttivo, mapping, setMapping,
    colonneNascoste, setColonneNascoste, setRigheMappate,
  } = useApp();
  const { puoMappare } = useAuth();

  const [nomeCliente, setNomeCliente] = useState(templateAttivo?.cliente || "");
  const [cartellaDestinazione, setCartellaDestinazione] = useState(templateAttivo?.cartellaDestinazione || "");
  const [sfogliando, setSfogliando] = useState(false);

  async function apriSelettoreCartella() {
    setSfogliando(true);
    setErrore("");
    try {
      const { percorso } = await scegliCartella();
      if (percorso) setCartellaDestinazione(percorso);
    } catch (err) {
      setErrore(err.message);
    } finally {
      setSfogliando(false);
    }
  }
  const [salvando, setSalvando] = useState(false);
  const [errore, setErrore] = useState("");
  const [mostraNascoste, setMostraNascoste] = useState(false);
  const [modaleEstrazione, setModaleEstrazione] = useState(null); // { chiave, nomeColonnaDestinazione } oppure null
  const [nuovaCombinazione, setNuovaCombinazione] = useState({ nome: "", sorgenti: ["", ""], separatore: "-" });

  // Suggerimenti di mapping calcolati dallo storico dei template (vedi
  // routes/api.js /suggerisci-mapping): per ogni campo del tracciato, la
  // colonna sorgente / il valore fisso usati più spesso in passato su file
  // simili. Si applicano SOLO ai campi ancora "manuale/vuoto" — mai sopra una
  // scelta già fatta dall'utente o ereditata da un template.
  const [suggerimenti, setSuggerimenti] = useState({}); // { <chiave>: { tipo, colonna|valore, vistoIn, suTotale, esatto, confidenza } }
  const [suggerimentiNascosti, setSuggerimentiNascosti] = useState(false);
  const [suggerimentiIgnorati, setSuggerimentiIgnorati] = useState([]); // [chiave] rifiutati a mano

  useEffect(() => {
    if (!fileCaricato || !Array.isArray(fileCaricato.headers) || fileCaricato.headers.length === 0) {
      setSuggerimenti({});
      return;
    }
    let annullato = false;
    (async () => {
      try {
        const { suggerimenti: s } = await suggerisciMapping(fileCaricato.headers, modulo);
        if (!annullato) setSuggerimenti(s || {});
      } catch {
        if (!annullato) setSuggerimenti({}); // un suggerimento mancato non è un errore da mostrare
      }
    })();
    return () => { annullato = true; };
  }, [fileCaricato, modulo]);

  // A differenza di prima, qui NON si torna più indietro se manca il file:
  // si può aprire il mapping di un template esistente anche senza aver
  // caricato un file cliente (es. da Home > Template clienti salvati >
  // Modifica). In quel caso le colonne sorgente si scrivono a mano invece
  // di sceglierle da un elenco, e anteprima/estrazione restano disattivate.
  useEffect(() => {
    if (!tracciato) navigate(percorsoBase);
  }, [tracciato, navigate, percorsoBase]);

  // Chi non ha il permesso di mappare non deve poter arrivare qui in nessun
  // modo (link diretto, tasto indietro del browser, ecc.): il server
  // rifiuterebbe comunque il salvataggio, ma è più chiaro rimandarlo subito
  // alla home invece di fargli vedere/compilare un editor che poi non può usare.
  useEffect(() => {
    if (!puoMappare) navigate(percorsoBase);
  }, [puoMappare, navigate, percorsoBase]);

  function aggiornaRegola(chiave, patch) {
    setMapping((prev) => ({
      ...prev,
      [chiave]: { ...(prev[chiave] || { tipo: "manuale" }), ...patch },
    }));
  }

  // Un campo è "libero" (un suggerimento può riempirlo) quando non ha ancora
  // nessuna regola vera: manca del tutto, o è "manuale", o è "sorgente"/"fisso"
  // ma senza valore scelto.
  function campoLibero(chiave) {
    const r = mapping[chiave];
    if (!r || r.tipo === "manuale") return true;
    if (r.tipo === "sorgente") return !r.colonna;
    if (r.tipo === "fisso") return !String(r.valore ?? "").trim();
    return false;
  }

  // I suggerimenti che ha senso mostrare adesso: quelli per campi ancora
  // liberi e non rifiutati a mano.
  const suggerimentiAttivi = useMemo(() => {
    const out = {};
    for (const [chiave, s] of Object.entries(suggerimenti)) {
      if (suggerimentiIgnorati.includes(chiave)) continue;
      if (!campoLibero(chiave)) continue;
      out[chiave] = s;
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggerimenti, suggerimentiIgnorati, mapping]);

  function regolaDaSuggerimento(s) {
    return s.tipo === "fisso"
      ? { tipo: "fisso", valore: s.valore }
      : { tipo: "sorgente", colonna: s.colonna, estrazione: undefined };
  }

  function applicaSuggerimento(chiave) {
    const s = suggerimenti[chiave];
    if (s) aggiornaRegola(chiave, regolaDaSuggerimento(s));
  }

  function applicaTuttiSuggerimenti() {
    setMapping((prev) => {
      const next = { ...prev };
      for (const [chiave, s] of Object.entries(suggerimentiAttivi)) {
        next[chiave] = { ...(next[chiave] || {}), ...regolaDaSuggerimento(s) };
      }
      return next;
    });
  }

  function nascondiColonna(chiave) {
    setColonneNascoste((prev) => (prev.includes(chiave) ? prev : [...prev, chiave]));
  }

  function mostraColonna(chiave) {
    setColonneNascoste((prev) => prev.filter((c) => c !== chiave));
  }

  const campiCombinati = mapping.__campiCombinati || [];

  // Nomi fra cui scegliere per costruire una combinazione: le colonne vere
  // del file, più quelle già combinate (per poterle incatenare).
  const nomiCampiDisponibili = fileCaricato
    ? [...fileCaricato.headers, ...campiCombinati.map((c) => c.nome)]
    : [];

  const righeOrigineEstese = useMemo(() => {
    if (!fileCaricato) return [];
    return fileCaricato.anteprima.map((riga) => applicaCampiCombinatiRiga(riga, campiCombinati));
  }, [fileCaricato, campiCombinati]);

  const anteprimaMappata = useMemo(() => {
    if (!tracciato || !fileCaricato) return [];
    return righeOrigineEstese.slice(0, 5).map((riga) => mappaRigaAnteprima(riga, mapping, tracciato.colonne));
  }, [tracciato, fileCaricato, mapping, righeOrigineEstese]);

  function aggiungiCombinazione() {
    const { nome, sorgenti, separatore } = nuovaCombinazione;
    if (!nome.trim() || sorgenti.some((s) => !s)) return;
    setMapping((prev) => ({
      ...prev,
      __campiCombinati: [...(prev.__campiCombinati || []), { nome: nome.trim(), sorgenti: [...sorgenti], separatore }],
    }));
    setNuovaCombinazione({ nome: "", sorgenti: ["", ""], separatore: "-" });
  }

  function rimuoviCombinazione(nome) {
    setMapping((prev) => ({
      ...prev,
      __campiCombinati: (prev.__campiCombinati || []).filter((c) => c.nome !== nome),
    }));
  }

  const colonneConValore = useMemo(() => {
    if (!tracciato) return [];
    return tracciato.colonne.filter((c) => {
      const r = mapping[c.chiave];
      return r && r.tipo !== "manuale";
    });
  }, [tracciato, mapping]);

  // Colonne obbligatorie per l'ERP (tracciato.json, campo "obbligatoria")
  // senza nessuna regola di mapping: usato solo per avvisare qui in fase di
  // mapping, il blocco vero e proprio del download (che controlla i valori
  // riga per riga, non solo se la regola esiste) è in Revisione.jsx.
  const colonneObbligatorieNonCompilate = useMemo(() => {
    if (!tracciato) return [];
    return tracciato.colonne.filter((c) => c.obbligatoria && (!mapping[c.chiave] || mapping[c.chiave].tipo === "manuale"));
  }, [tracciato, mapping]);

  async function procediARevisione(salvaComeTemplate) {
    setErrore("");
    if (salvaComeTemplate && !nomeCliente.trim()) {
      setErrore("Inserisci il nome del cliente per salvare il template.");
      return;
    }
    setSalvando(true);
    try {
      if (salvaComeTemplate) {
        await salvaTemplate({
          id: templateAttivo?.id, cliente: nomeCliente.trim(), mapping, colonneNascoste,
          cartellaDestinazione: cartellaDestinazione.trim(), modulo, impostazioniFile: fileCaricato?.impostazioniFile,
        });
      }
      // Revisione (pagina successiva) legge la cartella di destinazione da
      // templateAttivo, non dal campo qui sopra: senza aggiornarlo, un
      // cambio appena fatto al percorso — o il primo percorso mai impostato
      // su un template nuovo — non veniva usato per l'esportazione, restando
      // quello vecchio (o vuoto). Aggiornato anche quando NON si salva il
      // template ("Continua senza salvare"): quello che c'è scritto nel
      // campo in questo momento è quello che deve valere per il download.
      setTemplateAttivo((prev) => ({ ...(prev || {}), cliente: nomeCliente.trim() || prev?.cliente, cartellaDestinazione: cartellaDestinazione.trim() }));
      const { righe } = await applicaMapping({ fileId: fileCaricato.fileId, fileIds: lottoPdf?.file ? [fileCaricato.fileId, ...lottoPdf.file.map((f) => f.fileId)] : undefined, mapping, modulo });
      setRigheMappate(righe);
      navigate(`${percorsoBase}/revisione`);
    } catch (err) {
      setErrore(err.message);
    } finally {
      setSalvando(false);
    }
  }

  // Senza un file caricato non c'è nulla su cui applicare il mapping: si può
  // solo salvare il template e tornare alla Home (non si passa da Revisione).
  async function salvaTemplateSenzaFile() {
    setErrore("");
    if (!nomeCliente.trim()) {
      setErrore("Inserisci il nome del cliente per salvare il template.");
      return;
    }
    setSalvando(true);
    try {
      await salvaTemplate({
        id: templateAttivo?.id, cliente: nomeCliente.trim(), mapping, colonneNascoste,
        cartellaDestinazione: cartellaDestinazione.trim(), modulo, impostazioniFile: fileCaricato?.impostazioniFile,
      });
      navigate(percorsoBase);
    } catch (err) {
      setErrore(err.message);
    } finally {
      setSalvando(false);
    }
  }

  if (!tracciato) return null;

  const gruppi = tracciato.gruppi || [{ chiave: undefined, titolo: "Colonne" }];

  return (
    <div className="pagina">
      <section className="riquadro">
        <h2>
          {templateAttivo?.cliente
            ? `Mappa le colonne — modifica template "${templateAttivo.cliente}"`
            : `Mappa le colonne${fileCaricato ? ` — ${fileCaricato.nomeOriginale}` : ""}`}
        </h2>
        {!fileCaricato && (
          <p className="errore">
            Nessun file cliente caricato: le colonne sorgente vanno scritte a mano (senza elenco), e anteprima ed
            estrazione restano disattivate finché non carichi un file da "Carica file".
          </p>
        )}
        <p className="testo-secondario">
          Per ogni colonna del tracciato scegli da dove prendere il valore. Le colonne lasciate "manuale/vuoto"
          resteranno vuote nel file finale (potrai comunque compilarle a mano nella revisione). Le colonne che non
          userai mai per questo cliente puoi nasconderle dalla vista: restano comunque sempre presenti (vuote) nel
          file di export. Le colonne segnate con <span style={{ color: "var(--cf-danger-text)", fontWeight: 700 }}>*</span> sono
          obbligatorie per l'ERP: non possono restare vuote nel file finale.
        </p>
        {colonneObbligatorieNonCompilate.length > 0 && (
          <p className="errore">
            ⚠️ {colonneObbligatorieNonCompilate.length} colonne obbligatorie non ancora compilate: {colonneObbligatorieNonCompilate.map((c) => c.nome).join(", ")}.
            Puoi comunque salvare il template per riprenderlo più tardi, ma non potrai generare il file finale finché non sono compilate.
          </p>
        )}
        {colonneNascoste.length > 0 && (
          <label className="riga-controlli" style={{ marginBottom: "0.75rem" }}>
            <input type="checkbox" checked={mostraNascoste} onChange={(e) => setMostraNascoste(e.target.checked)} />
            {" "}Mostra le {colonneNascoste.length} colonne nascoste per questo template
          </label>
        )}

        {!suggerimentiNascosti && Object.keys(suggerimentiAttivi).length > 0 && (
          <div
            className="riga-controlli"
            style={{
              marginBottom: "0.75rem", padding: "0.5rem 0.75rem", borderRadius: 6,
              background: "var(--cf-bg-accent-soft, rgba(34,211,238,0.10))", border: "1px solid var(--cf-border)",
              flexWrap: "wrap",
            }}
          >
            <span>
              💡 <strong>{Object.keys(suggerimentiAttivi).length}</strong> camp{Object.keys(suggerimentiAttivi).length === 1 ? "o ha" : "i hanno"} un
              {" "}suggerimento dallo storico dei template — colonne o valori usati di solito per campi come questi.
            </span>
            <button className="btn-primario" onClick={applicaTuttiSuggerimenti}>Applica tutti</button>
            <button onClick={() => setSuggerimentiNascosti(true)}>Nascondi</button>
          </div>
        )}

        {gruppi.map((gruppo) => (
          <div key={gruppo.chiave} className="gruppo-mapping">
            <h3>{gruppo.titolo}</h3>
            <table className="tabella-mapping">
              <thead>
                <tr>
                  <th>Colonna destinazione</th>
                  <th>Tipo</th>
                  <th>Origine / Valore</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {tracciato.colonne
                  .filter((c) => c.gruppo === gruppo.chiave)
                  .filter((c) => mostraNascoste || !colonneNascoste.includes(c.chiave))
                  .map((col) => {
                    const regola = mapping[col.chiave] || { tipo: "manuale" };
                    const nascosta = colonneNascoste.includes(col.chiave);
                    return (
                      <tr key={col.chiave} style={nascosta ? { opacity: 0.5 } : undefined}>
                        <td>
                          {col.nome}
                          {col.obbligatoria && (
                            <span style={{ color: "var(--cf-danger-text)", fontWeight: 700 }} title="Campo obbligatorio per l'import ERP: non può restare vuoto">
                              {" "}*
                            </span>
                          )}
                        </td>
                        <td>
                          <select
                            value={regola.tipo}
                            onChange={(e) => aggiornaRegola(col.chiave, { tipo: e.target.value })}
                          >
                            {TIPI.map((t) => <option key={t.valore} value={t.valore}>{t.etichetta}</option>)}
                          </select>
                        </td>
                        <td>
                          {regola.tipo === "sorgente" && (
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                              {fileCaricato ? (
                                <select
                                  value={regola.colonna || ""}
                                  onChange={(e) => aggiornaRegola(col.chiave, { colonna: e.target.value, estrazione: undefined })}
                                >
                                  <option value="">-- scegli colonna file cliente --</option>
                                  {fileCaricato.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                                  {campiCombinati.length > 0 && (
                                    <optgroup label="Campi combinati">
                                      {campiCombinati.map((c) => <option key={c.nome} value={c.nome}>{c.nome}</option>)}
                                    </optgroup>
                                  )}
                                </select>
                              ) : (
                                <input
                                  type="text"
                                  placeholder="nome colonna file cliente (es. Article description)"
                                  value={regola.colonna || ""}
                                  onChange={(e) => aggiornaRegola(col.chiave, { colonna: e.target.value })}
                                />
                              )}
                              {regola.colonna && fileCaricato && (
                                regola.estrazione ? (
                                  <div className="riga-controlli">
                                    <span className="testo-secondario">
                                      estratto: "{estraiValore(righeOrigineEstese[0]?.[regola.colonna], regola.estrazione) || "—"}"
                                    </span>
                                    <button onClick={() => setModaleEstrazione({ chiave: col.chiave, nomeColonnaDestinazione: col.nome })}>
                                      Modifica estrazione
                                    </button>
                                    <button onClick={() => setModaleEstrazione({ chiave: col.chiave, nomeColonnaDestinazione: col.nome, daZero: true })}>
                                      🔄 Rifai da capo
                                    </button>
                                    <button onClick={() => aggiornaRegola(col.chiave, { estrazione: undefined })}>
                                      Usa valore intero
                                    </button>
                                  </div>
                                ) : (
                                  <button onClick={() => setModaleEstrazione({ chiave: col.chiave, nomeColonnaDestinazione: col.nome })}>
                                    ✂️ Estrai solo una parte
                                  </button>
                                )
                              )}
                              {regola.colonna && !fileCaricato && regola.estrazione && (
                                <span className="testo-secondario">estrazione impostata — carica un file per modificarla</span>
                              )}
                              {regola.colonna && (
                                <label
                                  className="riga-controlli testo-secondario"
                                  title="Per i file che stampano il punto al posto della virgola decimale (es. le distinte Dior): 1.0350 diventa 1,0350. Cambia solo i numeri semplici, il resto resta com'è."
                                >
                                  <input
                                    type="checkbox"
                                    checked={!!regola.numeroVirgola}
                                    onChange={(e) => aggiornaRegola(col.chiave, { numeroVirgola: e.target.checked || undefined })}
                                  />
                                  🔢 Numero: cambia il punto in virgola (1.0350 → 1,0350)
                                </label>
                              )}
                            </div>
                          )}
                          {regola.tipo === "fisso" && (
                            <input
                              type="text"
                              placeholder="valore fisso per tutte le righe"
                              value={regola.valore || ""}
                              onChange={(e) => aggiornaRegola(col.chiave, { valore: e.target.value })}
                            />
                          )}
                          {regola.tipo === "manuale" && <span className="testo-secondario">—</span>}
                          {!suggerimentiNascosti && suggerimentiAttivi[col.chiave] && (
                            <div className="riga-controlli" style={{ marginTop: "0.3rem", fontSize: "0.85em" }}>
                              <span className="testo-secondario">
                                💡 di solito:{" "}
                                {suggerimentiAttivi[col.chiave].tipo === "fisso"
                                  ? <>valore fisso «<strong>{suggerimentiAttivi[col.chiave].valore}</strong>»</>
                                  : <>dalla colonna «<strong>{suggerimentiAttivi[col.chiave].colonna}</strong>»{!suggerimentiAttivi[col.chiave].esatto && " (nome simile)"}</>}
                                {" "}({suggerimentiAttivi[col.chiave].vistoIn}/{suggerimentiAttivi[col.chiave].suTotale} template)
                              </span>
                              <button onClick={() => applicaSuggerimento(col.chiave)}>usa</button>
                              <button className="btn-link-danger" onClick={() => setSuggerimentiIgnorati((p) => [...p, col.chiave])} title="Non propormelo per questo campo">✕</button>
                            </div>
                          )}
                        </td>
                        <td>
                          {nascosta ? (
                            <button onClick={() => mostraColonna(col.chiave)} title="Torna a mostrare questa colonna nel mapping">
                              Mostra
                            </button>
                          ) : (
                            <button onClick={() => nascondiColonna(col.chiave)} title="Non uso questa colonna per questo cliente: nascondila dal mapping">
                              Nascondi
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        ))}
      </section>

      {fileCaricato && nomiCampiDisponibili.length >= 2 && (
        <section className="riquadro">
          <h2>Unisci campi</h2>
          <p className="testo-secondario" style={{ marginTop: 0 }}>
            Se il valore vero non sta in una sola colonna del file ma è l'unione di due (es. il vero numero ordine
            cliente = colonna "Ordine" + colonna "Riga"), scegline due qui sotto, un separatore, e il nome del campo
            nuovo: comparirà come colonna in più qui sotto nell'anteprima, e potrai sceglierlo come "colonna
            sorgente" per qualunque campo del tracciato, sopra.
          </p>
          <p className="riga-controlli">
            <select
              value={nuovaCombinazione.sorgenti[0]}
              onChange={(e) => setNuovaCombinazione((prev) => ({ ...prev, sorgenti: [e.target.value, prev.sorgenti[1]] }))}
            >
              <option value="">— primo campo —</option>
              {nomiCampiDisponibili.map((nome) => <option key={nome} value={nome}>{nome}</option>)}
            </select>
            <input
              type="text"
              value={nuovaCombinazione.separatore}
              onChange={(e) => setNuovaCombinazione((prev) => ({ ...prev, separatore: e.target.value }))}
              placeholder="separatore"
              style={{ width: "4rem", textAlign: "center" }}
              title='Testo messo fra i due valori, es. "-". Vuoto per accostarli senza nulla in mezzo.'
            />
            <select
              value={nuovaCombinazione.sorgenti[1]}
              onChange={(e) => setNuovaCombinazione((prev) => ({ ...prev, sorgenti: [prev.sorgenti[0], e.target.value] }))}
            >
              <option value="">— secondo campo —</option>
              {nomiCampiDisponibili.map((nome) => <option key={nome} value={nome}>{nome}</option>)}
            </select>
            <span>➡</span>
            <input
              type="text"
              value={nuovaCombinazione.nome}
              onChange={(e) => setNuovaCombinazione((prev) => ({ ...prev, nome: e.target.value }))}
              placeholder="nome campo nuovo"
            />
            <button
              onClick={aggiungiCombinazione}
              disabled={!nuovaCombinazione.nome.trim() || nuovaCombinazione.sorgenti.some((s) => !s)}
            >
              Aggiungi combinazione
            </button>
          </p>
          {campiCombinati.length > 0 && (
            <div className="riga-controlli">
              {campiCombinati.map((c) => (
                <span key={c.nome} className="riga-controlli" style={{ border: "1px solid var(--cf-border)", borderRadius: 4, padding: "0.2rem 0.5rem" }}>
                  <strong>{c.nome}</strong> = {c.sorgenti.join(` "${c.separatore}" `)}
                  {" "}
                  <button className="btn-link-danger" onClick={() => rimuoviCombinazione(c.nome)} title="Rimuovi">✕</button>
                </span>
              ))}
            </div>
          )}
        </section>
      )}

      {fileCaricato ? (
        <section className="riquadro">
          <h2>Anteprima risultato ({colonneConValore.length} / {tracciato.colonne.length} colonne mappate)</h2>
          <p className="testo-secondario">Calcolata sul file caricato: {fileCaricato.nomeOriginale}</p>
          <div className="scroll-orizzontale">
            <table className="tabella-anteprima">
              <thead>
                <tr>
                  {colonneConValore.map((c) => <th key={c.chiave}>{c.nome}</th>)}
                  {campiCombinati.map((c) => (
                    <th key={c.nome} title="Campo combinato: non fa parte del tracciato ERP, serve per essere scelto come colonna sorgente">
                      {c.nome} 🔗
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {anteprimaMappata.map((riga, i) => (
                  <tr key={i}>
                    {colonneConValore.map((c) => <td key={c.chiave}>{String(riga[c.chiave] ?? "")}</td>)}
                    {campiCombinati.map((c) => <td key={c.nome}>{String(righeOrigineEstese[i]?.[c.nome] ?? "")}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {colonneConValore.length === 0 && <p className="testo-secondario">Nessuna colonna mappata ancora.</p>}
        </section>
      ) : (
        <section className="riquadro">
          <h2>Anteprima risultato</h2>
          <p className="testo-secondario">Carica un file cliente da "Carica file" per vedere l'anteprima dei dati mappati.</p>
        </section>
      )}

      <section className="riquadro barra-azioni">
        <div className="riga-controlli">
          <label>Nome cliente (per salvare il template):</label>
          <input type="text" value={nomeCliente} onChange={(e) => setNomeCliente(e.target.value)} placeholder="es. Montblanc" />
        </div>
        <div className="riga-controlli">
          <label>Cartella di destinazione (opzionale):</label>
          <input
            type="text"
            value={cartellaDestinazione}
            onChange={(e) => setCartellaDestinazione(e.target.value)}
            placeholder={"es. \\\\server\\condivisa\\import oppure C:\\Import\\Montblanc"}
            style={{ minWidth: "22rem" }}
          />
          <button onClick={apriSelettoreCartella} disabled={sfogliando}>
            {sfogliando ? "Scegli la cartella nella finestra..." : "📁 Sfoglia..."}
          </button>
        </div>
        <p className="testo-secondario" style={{ marginTop: "0.5rem" }}>
          Se la imposti, ogni volta che scarichi il file finale per questo template ne verrà salvata automaticamente
          una copia anche in questo percorso locale o di rete (in aggiunta al download).
        </p>
        {errore && <p className="errore">{errore}</p>}
        <div className="riga-controlli">
          <button onClick={() => navigate(percorsoBase)} disabled={salvando}>Annulla</button>
          {fileCaricato ? (
            <>
              <button onClick={() => procediARevisione(false)} disabled={salvando}>Continua senza salvare</button>
              <button className="btn-primario" onClick={() => procediARevisione(true)} disabled={salvando}>
                {templateAttivo?.id ? "Aggiorna template e continua" : "Salva template e continua"}
              </button>
            </>
          ) : (
            <button className="btn-primario" onClick={salvaTemplateSenzaFile} disabled={salvando}>
              {templateAttivo?.id ? "Aggiorna template" : "Salva template"}
            </button>
          )}
        </div>
      </section>

      {modaleEstrazione && fileCaricato && (
        <EstrazioneModal
          nomeColonnaSorgente={mapping[modaleEstrazione.chiave]?.colonna}
          nomeColonnaDestinazione={modaleEstrazione.nomeColonnaDestinazione}
          esempi={righeOrigineEstese.map((r) => String(r[mapping[modaleEstrazione.chiave]?.colonna] ?? ""))}
          estrazioneIniziale={modaleEstrazione.daZero ? null : (mapping[modaleEstrazione.chiave]?.estrazione || null)}
          onConferma={(estrazione) => {
            aggiornaRegola(modaleEstrazione.chiave, { estrazione });
            setModaleEstrazione(null);
          }}
          onAnnulla={() => setModaleEstrazione(null)}
        />
      )}
    </div>
  );
}
